/**
 * Shared RenameField - AI sparkle suggest + optional date/todo prefix chips.
 * Used by Chats sidebar, conversation rename dialog, and document rename.
 */

import { useEffect, useMemo, useRef, useState } from 'react'
import { Calendar, Fingerprint, Hash, Loader2, Sparkles } from 'lucide-react'
import { cn } from '@/lib/utils'
import {
  segmentFilename,
  todayDatePrefix,
  type PrefixChunk,
} from '@/lib/rename-filename'
import { testIds } from '@/lib/test-ids'

export interface RenameFieldProps {
  value: string
  onChange: (value: string) => void
  /** Called when sparkle is clicked; parent runs suggest API and updates value. */
  onSuggest?: () => void | Promise<void>
  isSuggesting?: boolean
  suggestLabel?: string
  /** Hide sparkle when suggest is unavailable (e.g. no linked conversation). */
  showSuggest?: boolean
  dateOn?: boolean
  onDateToggle?: () => void
  showDateChip?: boolean
  dateLabel?: string
  todoId?: string | null
  todoOn?: boolean
  onTodoToggle?: () => void
  /** Trailing uniqueness token (pilot-feedback), shown as a chip when set. */
  uniqSuffix?: string | null
  uniqOn?: boolean
  onUniqToggle?: () => void
  autoFocus?: boolean
  disabled?: boolean
  className?: string
  inputClassName?: string
  onKeyDown?: (e: React.KeyboardEvent<HTMLInputElement>) => void
  onBlur?: (e: React.FocusEvent<HTMLInputElement>) => void
  id?: string
}

/** Shared rename input with optional AI suggest and document prefix chips. */
export function RenameField({
  value,
  onChange,
  onSuggest,
  isSuggesting = false,
  suggestLabel = 'AI suggest name',
  showSuggest = true,
  dateOn,
  onDateToggle,
  showDateChip = false,
  dateLabel,
  todoId = null,
  todoOn,
  onTodoToggle,
  uniqSuffix = null,
  uniqOn,
  onUniqToggle,
  autoFocus = false,
  disabled = false,
  className,
  inputClassName,
  onKeyDown,
  onBlur,
  id,
}: RenameFieldProps) {
  const inputRef = useRef<HTMLInputElement>(null)
  const [hoverChunk, setHoverChunk] = useState<PrefixChunk | null>(null)
  const dateText = dateLabel ?? todayDatePrefix()

  useEffect(() => {
    if (!autoFocus) return
    const t = window.setTimeout(() => {
      inputRef.current?.focus()
      inputRef.current?.select()
    }, 50)
    return () => window.clearTimeout(t)
  }, [autoFocus])

  const showTodoChip = Boolean(todoId)
  const showUniqChip = Boolean(uniqSuffix)
  const segments = useMemo(() => segmentFilename(value, todoId), [value, todoId])
  const showMirror = hoverChunk !== null
  const canSuggest = Boolean(showSuggest && onSuggest)

  return (
    <div className={cn('space-y-2', className)} data-testid={testIds.renameField.root}>
      <div className="relative flex items-center">
        {showMirror && (
          <div
            aria-hidden
            className="pointer-events-none absolute inset-0 flex items-center overflow-hidden rounded-md py-2 pl-3 pr-10 text-sm"
          >
            <span className="truncate font-mono">
              {segments.map((seg, i) => {
                const active = seg.key === hoverChunk
                return (
                  <span
                    key={`${seg.key}-${i}`}
                    className={cn(
                      active
                        ? 'font-bold text-primary underline decoration-primary/50 underline-offset-2'
                        : 'text-foreground/80',
                    )}
                  >
                    {seg.text}
                  </span>
                )
              })}
            </span>
          </div>
        )}
        <input
          id={id}
          ref={inputRef}
          value={value}
          disabled={disabled || isSuggesting}
          onChange={(e) => onChange(e.target.value)}
          onKeyDown={onKeyDown}
          onBlur={onBlur}
          data-testid={testIds.renameField.input}
          className={cn(
            'w-full rounded-md border border-input bg-background py-2 pl-3 text-sm outline-none focus:ring-1 focus:ring-primary disabled:opacity-50',
            canSuggest ? 'pr-10 font-mono' : 'pr-3',
            showMirror && 'caret-foreground text-transparent',
            inputClassName,
          )}
          aria-label="New name"
        />
        {canSuggest && (
          <button
            type="button"
            onMouseDown={(e) => e.preventDefault()}
            onClick={() => void onSuggest?.()}
            disabled={disabled || isSuggesting}
            title={suggestLabel}
            aria-label={suggestLabel}
            data-testid={testIds.renameField.suggest}
            className="absolute right-1.5 top-1/2 flex h-7 w-7 -translate-y-1/2 items-center justify-center rounded-md text-muted-foreground hover:bg-accent hover:text-foreground disabled:opacity-50"
          >
            {isSuggesting ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <Sparkles className="h-4 w-4" />
            )}
          </button>
        )}
      </div>

      {(showDateChip || showTodoChip || showUniqChip) && (
        <div className="flex flex-wrap items-center gap-2" data-testid={testIds.renameField.chips}>
          {showTodoChip && todoId && (
            <button
              type="button"
              onClick={onTodoToggle}
              disabled={disabled}
              onMouseEnter={() => {
                if (todoOn) setHoverChunk('todo')
              }}
              onMouseLeave={() => setHoverChunk(null)}
              data-testid={testIds.renameField.todoChip}
              className={cn(
                'inline-flex items-center gap-1.5 rounded-md border px-2 py-1 font-mono text-xs transition-colors',
                todoOn
                  ? 'border-primary/40 bg-primary/10 text-foreground'
                  : 'border-border bg-muted/40 text-muted-foreground opacity-60',
              )}
              title={todoOn ? 'Remove Basecamp todo id prefix' : 'Add Basecamp todo id prefix'}
            >
              <Hash className="h-3 w-3 shrink-0" />
              {todoId}
            </button>
          )}
          {showDateChip && (
            <button
              type="button"
              onClick={onDateToggle}
              disabled={disabled}
              onMouseEnter={() => {
                if (dateOn) setHoverChunk('date')
              }}
              onMouseLeave={() => setHoverChunk(null)}
              data-testid={testIds.renameField.dateChip}
              className={cn(
                'inline-flex items-center gap-1.5 rounded-md border px-2 py-1 font-mono text-xs transition-colors',
                dateOn
                  ? 'border-primary/40 bg-primary/10 text-foreground'
                  : 'border-border bg-muted/40 text-muted-foreground opacity-60',
              )}
              title={dateOn ? 'Remove date prefix' : 'Add date prefix'}
            >
              <Calendar className="h-3 w-3 shrink-0" />
              {dateText}
            </button>
          )}
          {showUniqChip && uniqSuffix && (
            <button
              type="button"
              onClick={onUniqToggle}
              disabled={disabled}
              onMouseEnter={() => {
                if (uniqOn) setHoverChunk('uniq')
              }}
              onMouseLeave={() => setHoverChunk(null)}
              data-testid={testIds.renameField.uniqChip}
              className={cn(
                'inline-flex items-center gap-1.5 rounded-md border px-2 py-1 font-mono text-xs transition-colors',
                uniqOn
                  ? 'border-primary/40 bg-primary/10 text-foreground'
                  : 'border-border bg-muted/40 text-muted-foreground opacity-60',
              )}
              title={
                uniqOn
                  ? 'Remove uniqueness suffix (filename collision guard)'
                  : 'Keep uniqueness suffix on filename'
              }
            >
              <Fingerprint className="h-3 w-3 shrink-0" />
              {uniqSuffix}
            </button>
          )}
        </div>
      )}
    </div>
  )
}
